<template>
  <div :class="wrapperClasses">
    <label v-if="showLabel">
      {{ label }}
    </label>
    <date-picker
      :id="fieldId"
      ref="datepicker"
      :append-to-body="appendToBody"
      :bootstrap-styling="true"
      :calendar-class="calendarClasses"
      :disabled="disabled"
      :disabled-dates="disabledDates"
      :first-day-of-week="firstDayOfWeek"
      :fixed-position="getFixedPosition"
      :format="customFormatter"
      full-month-name
      :highlighted="highlighted"
      :inline="inline"
      :input-class="inputClasses"
      :language="language"
      :maximum-view="maximumView"
      minimum-view="day"
      :model-value="selectedDate"
      :name="fieldName"
      :open-date="openDate"
      :parser="customParser"
      :placeholder="placeholder"
      :required="required"
      :typeable="typeable"
      :use-utc="useUtc"
      @blur="blur"
      @changed-decade="changedDecade"
      @changed-month="changedMonth"
      @changed-year="changedYear"
      @closed="closed"
      @focus="focus"
      @input="input"
      @opened="opened"
      @selected="select"
    >
      <template #dayCellContent="{ cell }">
        <slot
          :cell="cell"
          name="dayCellContent"
        />
      </template>

      <template #afterDateInput>
        <div
          v-if="!inline"
          class="input-group-append"
        >
          <dp-button
            v-if="!selectedDate"
            :size="size"
            tabindex="-1"
            type="button"
            @click="openDatepicker"
          >
            <dp-icon-calendar :size="iconSize"/>
          </dp-button>

          <dp-button
            v-if="selectedDate"
            :size="size"
            tabindex="-1"
            type="button"
            @click="clear"
          >
            <dp-icon-close :size="iconSize"/>
          </dp-button>
        </div>
      </template>

      <template #prevIntervalBtn>
        <slot name="DpPrevIntervalBtn">
          <dp-icon-caret-left :size="16"/>
        </slot>
      </template>

      <template #nextIntervalBtn>
        <slot name="DpNextIntervalBtn">
          <dp-icon-caret-right :size="16"/>
        </slot>
      </template>

      <template #calendarFooter>
        <div class="dp-datepicker-footer mt-1">
          <slot/>
        </div>
      </template>
    </date-picker>
  </div>
</template>

<script>
import {
  DpIconCalendar,
  DpIconCaretLeft,
  DpIconCaretRight,
  DpIconClose,
} from '@dp-ui-kit/icons';
import dayJs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import updateLocale from 'dayjs/plugin/updateLocale';

import { SIZE_LG, SIZE_MD } from '../../config/constants';
import uidMixin from '../../mixins/uid.mixin';
import DpButton from '../button/DpButton.vue';
import { FORM_CONTROL_SIZES } from '../input/config';
import {
  DATEPICKER_BASE_CLASS,
  DATEPICKER_CALENDAR_CLASS,
  DATEPICKER_CALENDAR_ITEM_CLASS,
  DATEPICKER_CALENDAR_ITEM_INLINE_CLASS,
  DATEPICKER_CALENDAR_ITEM_RANGE_FROM_CLASS,
  DATEPICKER_CALENDAR_ITEM_RANGE_TO_CLASS,
  DATEPICKER_CALENDAR_ITEM_SINGLE_DATE_CLASS,
  DATEPICKER_POSITIONS,
  DATEPICKER_RANGE_FROM_PREFIX,
  DATEPICKER_RANGE_TO_PREFIX,
} from './config';
import DatePicker from './datepicker/components/DatePicker.vue';
import Language from './datepicker/locale/Language';

dayJs.extend(advancedFormat);
dayJs.extend(customParseFormat);
dayJs.extend(updateLocale);

export default {
  name: 'DpDatepickerItem',

  components: {
    DatePicker,
    DpButton,
    DpIconCalendar,
    DpIconCaretLeft,
    DpIconCaretRight,
    DpIconClose,
  },

  mixins: [
    uidMixin,
  ],

  props: {
    /**
     * @model modelValue
     */
    modelValue: {
      type: [Date, Object],
      default: null,
    },
    appendToBody: {
      type: Boolean,
      default: false,
    },
    highlighted: {
      type: Object,
      required: true,
    },
    placeholder: {
      type: String,
      required: true,
    },
    inputClass: {
      type: [String, Object],
      required: true,
    },
    name: {
      type: String,
      required: true,
    },
    id: {
      type: String,
      required: true,
    },
    format: {
      type: String,
      required: true,
    },
    showLabel: {
      type: Boolean,
      required: true,
    },
    maximumView: {
      type: String,
      required: true,
    },
    /**
     * @values sm, md, lg
     * @default md
     * */
    size: {
      type: String,
      default: SIZE_MD,
      required: false,
      validator: (value) => FORM_CONTROL_SIZES.includes(value),
    },
    firstDayOfWeek: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      required: true,
    },
    translations: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      required: true,
      default: false,
    },
    disabledDates: {
      type: Object,
      required: true,
    },
    inline: {
      type: Boolean,
      required: true,
    },
    typeable: {
      type: Boolean,
      required: false,
      default: false,
    },
    openDate: {
      type: [String, Date],
      required: false,
      default: null,
    },
    useUtc: {
      type: Boolean,
      required: false,
      default: false,
    },
    required: {
      type: Boolean,
      required: false,
      default: false,
    },
    fixedPosition: {
      type: String,
      required: false,
      default: 'auto',
      validator: (position) => DATEPICKER_POSITIONS.includes(position),
    },
  },

  emits: [
    'select',
    'update:modelValue',
    'clear',
    'blur',
    'changedDecade',
    'changedMonth',
    'changedYear',
    'closed',
    'focus',
    'input',
    'opened',
  ],

  data: () => ({
    selectedDate: null,
  }),

  computed: {
    dayJsFormat() {
      let { format } = this;

      format = format.replaceAll('d', 'D');
      format = format.replaceAll('y', 'Y');
      format = format.replaceAll('E', 'ddd');

      return format;
    },
    getFixedPosition() {
      if (this.fixedPosition === 'auto') {
        return '';
      }

      return this.fixedPosition;
    },
    inputClasses() {
      const { inputClass, size } = this;
      const inputSizeClass = `form-control-${size}`;

      if (Array.isArray(inputClass)) {
        return [inputSizeClass, ...inputClass];
      }

      if (typeof inputClass === 'object') {
        return { inputSizeClass: true, ...inputClass };
      }

      return inputClass ? `${inputClass} ${inputSizeClass}` : `${inputSizeClass}`;
    },

    fieldId() {
      const { id, _uid } = this;
      const splitId = id.split('|');
      const realId = splitId[1] || _uid;

      if (id && id.includes(DATEPICKER_RANGE_FROM_PREFIX)) {
        return `${DATEPICKER_BASE_CLASS}-${realId}-from`;
      }

      if (id && id.includes(DATEPICKER_RANGE_TO_PREFIX)) {
        return `${DATEPICKER_BASE_CLASS}-${realId}-to`;
      }

      if (id) {
        return `${DATEPICKER_BASE_CLASS}-${id}`;
      }

      return `${DATEPICKER_BASE_CLASS}-${_uid}`;
    },

    fieldName() {
      const { name, _uid } = this;
      const splitName = name.split('|');
      const realName = splitName[1] || _uid;

      if (name && name.includes(DATEPICKER_RANGE_FROM_PREFIX)) {
        return `${DATEPICKER_BASE_CLASS}-${realName}_from`;
      }

      if (name && name.includes(DATEPICKER_RANGE_TO_PREFIX)) {
        return `${DATEPICKER_BASE_CLASS}-${realName}_to`;
      }

      if (name) {
        return `${DATEPICKER_BASE_CLASS}-${name}`;
      }

      return `${DATEPICKER_BASE_CLASS}-${_uid}`;
    },

    iconSize() {
      if (this.size === SIZE_LG) {
        return 18;
      }

      return 16;
    },

    wrapperClasses() {
      const classes = [DATEPICKER_CALENDAR_ITEM_CLASS];

      return classes;
    },

    calendarClasses() {
      const classes = [DATEPICKER_CALENDAR_CLASS];
      const { name } = this;

      if (this.inline) {
        classes.push(DATEPICKER_CALENDAR_ITEM_INLINE_CLASS);
      }

      if (name.length > 0 && name.includes(DATEPICKER_RANGE_FROM_PREFIX)) {
        classes.push(DATEPICKER_CALENDAR_ITEM_RANGE_FROM_CLASS);
      }

      if (name.length > 0 && name.includes(DATEPICKER_RANGE_TO_PREFIX)) {
        classes.push(DATEPICKER_CALENDAR_ITEM_RANGE_TO_CLASS);
      }

      if (name.length === 0 && !name.includes(DATEPICKER_RANGE_FROM_PREFIX)) {
        classes.push(DATEPICKER_CALENDAR_ITEM_SINGLE_DATE_CLASS);
      }

      return classes;
    },

    language() {
      const { language, months, monthsAbbr, days } = this.translations;

      return new Language(language, months, monthsAbbr, days);
    },
  },

  watch: {
    modelValue: {
      handler(date) {
        this.setDate(date);
      },
      immediate: true,
    },
  },
  methods: {
    updateDayJsLocale() {
      const { days, months, monthsAbbr } = this.translations;

      dayJs.updateLocale(
        'en',
        {
          weekdaysShort: days,
          months,
          monthsShort: monthsAbbr,
        },
      );
    },
    customFormatter(date) {
      this.updateDayJsLocale();

      return dayJs(date).format(this.dayJsFormat);
    },
    customParser(date) {
      this.updateDayJsLocale();

      return dayJs(date, this.dayJsFormat);
    },
    openDatepicker() {
      this.$refs.datepicker.open();
      this.$refs.datepicker.$el.querySelector('input').focus();
    },

    select(date) {
      this.$emit('select', date);
      this.$emit('update:modelValue', date);
    },

    setDate(date) {
      this.selectedDate = date;
    },

    clear() {
      this.selectedDate = null;
      this.$emit('clear');
    },

    blur() {
      this.$emit('blur');
    },

    changedDecade(date) {
      this.$emit('changedDecade', date);
    },

    changedMonth(date) {
      this.$emit('changedMonth', date);
    },

    changedYear(date) {
      this.$emit('changedYear', date);
    },

    closed() {
      this.$emit('closed');
    },

    focus() {
      this.$emit('focus');
    },

    input(date) {
      this.$emit('input', date);
    },

    opened() {
      this.$emit('opened');
    },

  },
};
</script>

<style lang="scss">
@use './styles/style.scss';
</style>
